import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GoogleLogo } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;

function useAuth() {
  const location = useLocation();
  const [token, setToken] = useState(() => localStorage.getItem("pta_portal_token"));
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // If OAuth callback is in the URL, App-level handler processes it.
    if (window.location.hash?.includes("session_id=")) { setChecking(false); return; }
    (async () => {
      // First try cookie-based session (Google)
      try {
        const r = await axios.get(`${BACKEND}/api/auth/me`, { withCredentials: true });
        setUser(r.data);
        setChecking(false);
        return;
      } catch (_) { /* fall through */ }
      // Legacy JWT bearer
      if (token) {
        try {
          const r = await axios.get(`${BACKEND}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
          setUser(r.data);
        } catch (_) {
          localStorage.removeItem("pta_portal_token"); setToken(null); setUser(null);
        }
      }
      setChecking(false);
    })();
  }, [token]);

  const setJwt = (t) => {
    if (t) { localStorage.setItem("pta_portal_token", t); } else { localStorage.removeItem("pta_portal_token"); }
    setToken(t);
  };
  const logout = async () => {
    try { await axios.post(`${BACKEND}/api/auth/logout`, {}, { withCredentials: true }); } catch (_) {}
    setJwt(null); setUser(null);
  };
  return { token, user, setUser, setJwt, logout, checking };
}

function GoogleButton() {
  const onClick = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button
      type="button"
      data-testid="google-signin"
      onClick={onClick}
      className="w-full flex items-center justify-center gap-3 py-3 border hairline-navy bg-[var(--pta-cream)] text-[var(--pta-navy)] font-mono-fine text-[11px] tracking-[0.2em] uppercase hover:bg-[var(--pta-navy)] hover:text-[var(--pta-gold)] transition-colors"
    >
      <GoogleLogo size={18} weight="bold" />
      Continue with Google
    </button>
  );
}

export default function Portal() {
  const { token, user, setUser, setJwt, logout, checking } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [docs, setDocs] = useState([]);
  const [file, setFile] = useState(null);

  const set = (k,v)=>setForm(f=>({...f,[k]:v}));

  const authHeader = () => {
    // Prefer OAuth cookie (via withCredentials). Fall back to legacy JWT bearer.
    if (token) return { Authorization: `Bearer ${token}` };
    return {};
  };

  const fetchDocs = async () => {
    try {
      const r = await axios.get(`${BACKEND}/api/portal/documents`, { headers: authHeader(), withCredentials: true });
      setDocs(r.data);
    } catch (_) { setDocs([]); }
  };
  useEffect(() => { if (user) fetchDocs(); /* eslint-disable-next-line */ }, [user]);

  const submitAuth = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const url = mode === "login" ? "/api/portal/login" : "/api/portal/signup";
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const r = await axios.post(`${BACKEND}${url}`, payload);
      setJwt(r.data.token);
      setUser({ user_id: r.data.user.id, email: r.data.user.email, name: r.data.user.name, source: "jwt" });
      toast.success(mode === "login" ? "Welcome back." : "Account created.");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const uploadFile = async (e) => {
    e.preventDefault();
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try {
      await axios.post(`${BACKEND}/api/portal/documents`, fd, { headers: { ...authHeader(), "Content-Type": "multipart/form-data" }, withCredentials: true });
      setFile(null); document.getElementById("filein").value = "";
      toast.success("Uploaded"); fetchDocs();
    } catch { toast.error("Upload failed"); }
    setBusy(false);
  };

  if (checking) {
    return <section className="bg-[var(--pta-cream)] pt-24 pb-24 min-h-[60vh]"><div className="max-w-[500px] mx-auto px-6 text-center"><div className="chapter-num">Client Portal</div><div className="mt-4 font-serif-display text-3xl text-[var(--pta-navy)]">Checking session…</div></div></section>;
  }

  if (!user) {
    return (
      <section className="bg-[var(--pta-cream)] pt-16 md:pt-24 pb-24 min-h-[70vh]">
        <div className="max-w-[500px] mx-auto px-6">
          <div className="chapter-num">Client Portal</div>
          <h1 className="font-serif-display text-5xl md:text-6xl text-[var(--pta-navy)] mt-4">{mode==="login"?"Sign in.":"Create account."}</h1>

          <div className="mt-8 border hairline-navy p-6">
            <GoogleButton />
            <div className="my-6 flex items-center gap-3 text-[var(--pta-ink)]/50 font-mono-fine text-[10px] tracking-[0.24em] uppercase">
              <div className="flex-1 h-px bg-[var(--pta-line)]" /> or <div className="flex-1 h-px bg-[var(--pta-line)]" />
            </div>
            <form onSubmit={submitAuth} className="space-y-5">
              {mode==="signup" && <div><Label className="overline">Name</Label><Input data-testid="portal-name" required value={form.name} onChange={e=>set('name',e.target.value)} className="mt-2" /></div>}
              <div><Label className="overline">Email</Label><Input data-testid="portal-email" type="email" required value={form.email} onChange={e=>set('email',e.target.value)} className="mt-2" /></div>
              <div><Label className="overline">Password</Label><Input data-testid="portal-password" type="password" required value={form.password} onChange={e=>set('password',e.target.value)} className="mt-2" /></div>
              <button data-testid="portal-submit" className="btn-pta w-full justify-center" disabled={busy}>{busy?"…":(mode==="login"?"Sign in":"Sign up")}</button>
              <button type="button" data-testid="portal-toggle" onClick={()=>setMode(mode==="login"?"signup":"login")} className="w-full text-center font-mono-fine text-[11px] tracking-widest uppercase text-[var(--pta-gold)]">{mode==="login"?"Create account →":"← Have an account? Sign in"}</button>
            </form>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-[var(--pta-cream)] pt-16 md:pt-24 pb-24 min-h-[70vh]">
      <div className="max-w-[1100px] mx-auto px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {user.picture && <img src={user.picture} alt={user.name} className="w-14 h-14 border hairline-navy object-cover" />}
            <div>
              <div className="chapter-num">Client Portal</div>
              <h1 className="font-serif-display text-4xl md:text-5xl text-[var(--pta-navy)] mt-2">Hi, {(user.name || "there").split(" ")[0]}.</h1>
              <div className="text-xs text-[var(--pta-ink)]/60 font-mono-fine tracking-widest uppercase mt-1">{user.email}</div>
            </div>
          </div>
          <button data-testid="portal-logout" onClick={logout} className="btn-pta-outline">Sign out</button>
        </div>

        {user.role === "admin" && (
          <div className="mt-6 border hairline-gold bg-[var(--pta-navy)] text-[var(--pta-cream)] p-5 flex items-center justify-between">
            <div>
              <div className="font-mono-fine text-[10px] tracking-[0.22em] uppercase text-[var(--pta-gold)]">Admin access detected</div>
              <div className="font-serif-display text-2xl mt-1">Manage the site + all enquiries</div>
            </div>
            <Link data-testid="portal-admin-link" to="/admin" className="btn-pta-gold">Open Admin →</Link>
          </div>
        )}

        <form onSubmit={uploadFile} className="mt-10 border hairline-navy p-6 flex flex-col md:flex-row gap-4 md:items-end">
          <div className="flex-1"><Label className="overline">Upload document</Label>
            <input id="filein" data-testid="portal-file" type="file" onChange={(e)=>setFile(e.target.files?.[0]||null)} className="mt-2 block w-full text-sm" />
          </div>
          <button data-testid="portal-upload" className="btn-pta" disabled={busy||!file}>{busy?"…":"Upload"}</button>
        </form>

        <div className="mt-8 border hairline-navy">
          <div className="grid grid-cols-12 border-b hairline bg-[var(--pta-paper)] font-mono-fine text-[10px] tracking-[0.22em] uppercase text-[var(--pta-ink)]/60">
            <div className="col-span-6 p-4">Filename</div>
            <div className="col-span-2 p-4">Size</div>
            <div className="col-span-4 p-4">Uploaded</div>
          </div>
          {docs.length===0 && <div className="p-8 text-center text-[var(--pta-ink)]/60">No documents yet.</div>}
          {docs.map((d,i)=>(
            <div key={i} data-testid={`doc-${i}`} className="grid grid-cols-12 border-b hairline last:border-b-0">
              <div className="col-span-6 p-4 font-serif-display text-lg text-[var(--pta-navy)]">{d.filename}</div>
              <div className="col-span-2 p-4 font-mono-fine text-sm text-[var(--pta-ink)]/70">{(d.size/1024).toFixed(1)} KB</div>
              <div className="col-span-4 p-4 font-mono-fine text-xs text-[var(--pta-ink)]/60">{new Date(d.uploaded_at).toLocaleString()}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
